// Notifications (malfunction reports): list + detail with the lifecycle actions.
// Flow: operator reports (NewNotificationWizard, exists) -> this screen tracks it:
// put in progress (mechanic, canConfirm) -> convert to order (canCreateOrder) -> close
// (canApprove). The list is the shared touch-first pattern from OrdersList (Card rows,
// null=loading ladder); the detail owns its action state like OrderDetail's panels.
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { ArrowLeft, BellRing, Play, CheckCircle2, Wrench, RefreshCw } from 'lucide-react';
import { useTheme } from '../../../core/ui/ThemeContext';
import { useLanguage } from '../../../core/i18n/LanguageContext';
import { Card, Button, Row, StatusBadge } from './ui';
import {
  listNotifications, getNotification, notificationInProgress, closeNotification,
  createOrderForNotification, listNotificationPhotos, uploadNotificationPhoto,
} from '../api';

/** Photos ("broken like THIS") — portal-side storage via the shared files module; SAP never
 *  sees them (contract photoRefs). Capture uses the phone camera on mobile. */
function PhotosCard({ user, qmnum, plant, isDarkMode, t }) {
  const [photos, setPhotos] = useState(null);
  const [busy, setBusy] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let alive = true;
    // /api/files answers a bare array; listNotificationPhotos normalises both of its branches to
    // one, so there is no `.files` key to read here (audit finding 05).
    listNotificationPhotos(user, qmnum.replace(/^0+/, ''))
      .then((rows) => { if (alive) setPhotos(Array.isArray(rows) ? rows : []); })
      .catch(() => { if (alive) setPhotos([]); });
    return () => { alive = false; };
  }, [user, qmnum, reloadKey]);

  const onPick = async (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = '';
    if (!file) return;
    setBusy(true);
    setUploadError(null);
    try {
      await uploadNotificationPhoto(user, qmnum.replace(/^0+/, ''), file, plant);
      setReloadKey((k) => k + 1);
    } catch (err) {
      // A swallowed failure is indistinguishable from a thumbnail that simply did not load, so
      // the technician leaves believing the photo is there (audit finding 05). Say it instead.
      setUploadError(err && err.tooLarge ? t('pm.photos.tooLarge') : t('pm.photos.uploadFailed'));
    }
    setBusy(false);
  };

  return (
    <Card className="mb-3">
      <div className="flex items-center justify-between">
        <p className={`text-sm font-semibold ${isDarkMode ? 'text-white/80' : 'text-slate-700'}`}>
          {t('pm.photos.title')}
        </p>
        <label className={`text-xs font-medium cursor-pointer underline ${isDarkMode ? 'text-white/60' : 'text-slate-500'}`}>
          {busy ? '…' : t('pm.photos.add')}
          <input type="file" accept="image/*" capture="environment" className="hidden" onChange={onPick} disabled={busy} />
        </label>
      </div>
      {uploadError && (
        <p className={`mt-1 text-xs ${isDarkMode ? 'text-rose-300' : 'text-rose-600'}`}>{uploadError}</p>
      )}
      {photos && photos.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-2">
          {photos.map((p) => (
            // contentUrl carries the capability token (?k=…) the content route now demands for
            // every non-catalog file (backend/routes/files.js:83, audit finding 12). A hand-built
            // /api/files/<id>/content answers 404 for PM photos — always take the URL the list
            // row hands over.
            <a key={p.id} href={p.contentUrl} target="_blank" rel="noreferrer">
              <img src={p.contentUrl} alt={p.original_name || 'photo'}
                className="w-20 h-20 object-cover rounded-xl" />
            </a>
          ))}
        </div>
      )}
    </Card>
  );
}

/** One tappable list row. */
function NotifRow({ n, onOpen, isDarkMode, t }) {
  return (
    <button type="button" onClick={() => onOpen(n.notificationId)} className="w-full text-left">
      <Card className="mb-3">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className={`font-semibold truncate ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
              {n.description || n.notificationId}
            </div>
            <div className={`text-sm mt-0.5 ${isDarkMode ? 'text-white/50' : 'text-slate-500'}`}>
              {n.notificationId}{n.equipmentId ? ` · ${t('pm.notif.equipment')} ${n.equipmentId}` : ''}
              {n.createdAt ? ` · ${n.createdAt}` : ''}
            </div>
          </div>
          <StatusBadge status={n.status} />
        </div>
      </Card>
    </button>
  );
}

/** Detail + lifecycle actions for ONE notification. */
export function NotificationDetail({ me, user, notificationId, onBack, onOpenOrder }) {
  const { isDarkMode } = useTheme();
  const { t } = useLanguage();
  const role = me.profile.id;
  const rights = me.profile.functionRights;

  const [n, setN] = useState(null);
  const [missing, setMissing] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState(null);
  // Convert-to-order choices come from the instance scope; the server re-validates.
  const [orderType, setOrderType] = useState(me.profile.scope.orderTypes[0]);
  const [workCenter, setWorkCenter] = useState(me.profile.scope.workCenters[0]);
  // Malfunction end defaults to "now", minute precision — good enough for MTTR.
  const [mlfnEnd, setMlfnEnd] = useState(() => new Date(Date.now() - new Date().getTimezoneOffset() * 60000).toISOString().slice(0, 16));
  // ONE KEY PER ACTION, minted once per screen instance (NewOrderWizard.js:58 is the pattern).
  // The ledger looks a key up without comparing the action kind, so a single shared key lets a
  // timed-out close replay as an in-progress: the user presses the second button and gets a
  // cheerful success for a write that never happened (audit finding 20).
  const idemStart = useRef(crypto.randomUUID());
  const idemConvert = useRef(crypto.randomUUID());
  const idemClose = useRef(crypto.randomUUID());
  const seq = useRef(0);

  const load = useCallback(async () => {
    const mySeq = ++seq.current;
    try {
      const res = await getNotification(user, notificationId, role);
      if (seq.current === mySeq) setN(res);
    } catch (e) {
      if (seq.current !== mySeq) return;
      if (e?.status === 404) setMissing(true); else setLoadError(true);
    }
  }, [user, notificationId, role]);
  useEffect(() => { load(); }, [load]);

  // `keyRef` is the key of the action being run — only THAT action's key is rotated after a
  // success, so a retry of a different button still carries its own untouched key.
  const act = async (keyRef, fn) => {
    setBusy(true);
    setActionError(null);
    try {
      const res = await fn();
      if (res && res.ok === false) setActionError(res.error || t('pm.notif.actionFailed'));
      else {
        keyRef.current = crypto.randomUUID();
        await load();
        return res;
      }
    } catch (e) {
      setActionError(e.message);
    } finally {
      setBusy(false);
    }
    return null;
  };

  const sub = isDarkMode ? 'text-white/50' : 'text-slate-500';

  if (loadError) return <Card><p className={sub}>{t('pm.common.loadError')}</p></Card>;
  if (missing) return <Card><p className={sub}>{t('pm.notif.notFound')}</p></Card>;
  if (n === null) return <Card><p className={sub}>{t('pm.common.loading')}</p></Card>;

  const isOpen = n.status === 'OSNO';
  const closed = n.status === 'NOCO';

  return (
    <div>
      <button type="button" onClick={onBack} className={`flex items-center gap-1 mb-3 text-sm font-medium ${isDarkMode ? 'text-white/60 hover:text-white' : 'text-slate-500 hover:text-slate-800'}`}>
        <ArrowLeft size={16} /> {t('pm.common.back')}
      </button>

      <Card className="mb-3">
        <div className="flex items-start justify-between gap-3 mb-3">
          <h2 className={`text-lg font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{n.description}</h2>
          <StatusBadge status={n.status} />
        </div>
        <div className="space-y-1.5 text-sm">
          <Row label={t('pm.notif.number')} value={n.notificationId} info={t('pm.info.notifNumber')} />
          {n.equipmentId && <Row label={t('pm.notif.equipment')} value={`${n.equipmentId}${n.equipmentText ? ` — ${n.equipmentText}` : ''}`} info={t('pm.info.equipment')} />}
          {n.priority && <Row label={t('pm.notif.priority')} value={t(`pm.priority.${n.priority}`) !== `pm.priority.${n.priority}` ? t(`pm.priority.${n.priority}`) : n.priority} info={t('pm.info.priority')} />}
          <Row label={t('pm.notif.plant')} value={n.plant} info={t('pm.info.plant')} />
          {n.createdAt && <Row label={t('pm.notif.createdAt')} value={n.createdAt} />}
          {n.orderId && (
            <Row label={t('pm.notif.order')} value={
              <button type="button" className="underline" onClick={() => onOpenOrder && onOpenOrder(n.orderId)}>{n.orderId}</button>
            } info={t('pm.info.notifOrder')} />
          )}
        </div>
      </Card>

      {actionError && (
        <Card className="mb-3">
          <p className={isDarkMode ? 'text-rose-300 text-sm' : 'text-rose-600 text-sm'}>{actionError}</p>
        </Card>
      )}

      {/* Lifecycle actions, gated by right AND state — the server stays authoritative. */}
      {!closed && (
        <div className="space-y-3">
          {isOpen && rights.canConfirm && (
            <Button variant="primary" disabled={busy}
              onClick={() => act(idemStart, () => notificationInProgress(user, n.notificationId, idemStart.current, role))}>
              <Play size={20} /> {t('pm.notif.startWork')}
            </Button>
          )}
          {!n.orderId && rights.canCreateOrder && (
            <Card>
              <p className={`text-sm font-semibold mb-2 ${isDarkMode ? 'text-white/80' : 'text-slate-700'}`}>
                {t('pm.notif.convertTitle')}
              </p>
              <div className="flex gap-2 mb-3">
                <select value={orderType} onChange={(e) => setOrderType(e.target.value)}
                  className={`flex-1 rounded-xl px-3 py-2 ${isDarkMode ? 'bg-white/10 text-white' : 'bg-slate-100 text-slate-800'}`}>
                  {me.profile.scope.orderTypes.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
                <select value={workCenter} onChange={(e) => setWorkCenter(e.target.value)}
                  className={`flex-1 rounded-xl px-3 py-2 ${isDarkMode ? 'bg-white/10 text-white' : 'bg-slate-100 text-slate-800'}`}>
                  {me.profile.scope.workCenters.map((w) => <option key={w} value={w}>{w}</option>)}
                </select>
              </div>
              <Button variant="confirm" disabled={busy}
                onClick={async () => {
                  const res = await act(idemConvert, () => createOrderForNotification(user, n.notificationId, { orderType, workCenter }, idemConvert.current, role));
                  if (res && res.orderId && onOpenOrder) onOpenOrder(res.orderId.replace(/^0+/, ''));
                }}>
                <Wrench size={20} /> {t('pm.notif.convert')}
              </Button>
            </Card>
          )}
          {rights.canApprove && (
            <Card>
              {/* Breakdown reports ask ONE extra question: when did the malfunction end?
                  That single field is what makes MTTR/availability reports real. */}
              {n.breakdown && (
                <div className="mb-2">
                  <label className={`text-xs ${sub}`}>{t('pm.notif.mlfnEndLabel')}</label>
                  <input type="datetime-local" value={mlfnEnd} onChange={(e) => setMlfnEnd(e.target.value)}
                    className={`w-full rounded-xl px-3 py-2 ${isDarkMode ? 'bg-white/10 text-white' : 'bg-slate-100 text-slate-800'}`} />
                </div>
              )}
              <Button variant="secondary" disabled={busy}
                onClick={() => act(idemClose, () => closeNotification(user, n.notificationId, idemClose.current, role,
                  n.breakdown && mlfnEnd ? { malfunctionEnd: mlfnEnd } : null))}>
                <CheckCircle2 size={20} /> {t('pm.notif.close')}
              </Button>
            </Card>
          )}
        </div>
      )}

      <div className="mt-3">
        <PhotosCard user={user} qmnum={n.notificationId} plant={n.plant} isDarkMode={isDarkMode} t={t} />
      </div>
    </div>
  );
}

/** List of the plant's notifications. */
export default function NotificationsScreen({ me, user, onOpen, onBack }) {
  const { isDarkMode } = useTheme();
  const { t } = useLanguage();
  const role = me.profile.id;

  const [data, setData] = useState(null);
  const [loadError, setLoadError] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let alive = true;
    setData(null);
    setLoadError(false);
    listNotifications(user, { plant: me.activePlant }, role)
      .then((res) => { if (alive) setData(res.items || []); })
      .catch(() => { if (alive) setLoadError(true); });
    return () => { alive = false; };
  }, [user, me.activePlant, role, reloadKey]);

  const sub = isDarkMode ? 'text-white/50' : 'text-slate-500';

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <button type="button" onClick={onBack} className={`flex items-center gap-1 text-sm font-medium ${isDarkMode ? 'text-white/60 hover:text-white' : 'text-slate-500 hover:text-slate-800'}`}>
          <ArrowLeft size={16} /> {t('pm.common.back')}
        </button>
        <button type="button" onClick={() => setReloadKey((k) => k + 1)} aria-label={t('pm.common.retry')}
          className={`p-2 rounded-full ${isDarkMode ? 'text-white/60 hover:text-white' : 'text-slate-500 hover:text-slate-800'}`}>
          <RefreshCw size={16} />
        </button>
      </div>
      <h2 className={`flex items-center gap-2 text-lg font-bold mb-4 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
        <BellRing size={20} /> {t('pm.notif.title')}
      </h2>
      {loadError ? (
        <Card><p className={sub}>{t('pm.common.loadError')} <button type="button" className="underline" onClick={() => setReloadKey((k) => k + 1)}>{t('pm.common.retry')}</button></p></Card>
      ) : data === null ? (
        <Card><p className={sub}>{t('pm.common.loading')}</p></Card>
      ) : data.length === 0 ? (
        <Card><p className={sub}>{t('pm.notif.empty')}</p></Card>
      ) : (
        data.map((n) => <NotifRow key={n.notificationId} n={n} onOpen={onOpen} isDarkMode={isDarkMode} t={t} />)
      )}
    </div>
  );
}
